// src/pages/auth/LoginPage.js - Enhanced with QR code parameter detection (preserving existing design)
import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Container,
  Paper,
  InputAdornment,
  IconButton,
  Chip,
  Divider,
  FormControlLabel,
  Checkbox,
  Link
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import EmailIcon from '@mui/icons-material/Email';
import LockIcon from '@mui/icons-material/Lock';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import EvStationIcon from '@mui/icons-material/EvStation';
import QrCodeIcon from '@mui/icons-material/QrCode';

// Validation schema
const schema = yup.object({
  email: yup
    .string()
    .email('Invalid email format')
    .required('Email is required'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
});

const LoginPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { login, isAuthenticated, isLoading, error, clearError } = useAuth();
  const [showPassword, setShowPassword] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  
  // NEW: QR code parameters
  const [chargerName, setChargerName] = useState('');
  const [connectorNumber, setConnectorNumber] = useState('');
  const [isQrLogin, setIsQrLogin] = useState(false);

  // NEW: Check for QR code parameters on component mount
  useEffect(() => {
    const ch = searchParams.get('ch'); // charger_name
    const co = searchParams.get('co'); // connector_number
    
    if (ch && co) {
      setChargerName(ch);
      setConnectorNumber(co);
      setIsQrLogin(true);
      console.log('🔍 QR Code Login detected:', { charger: ch, connector: co });
    }
  }, [searchParams]);

  // Get redirect path from location state or default to dashboard
  // For QR login, don't use the 'from' redirect as we want the QR-specific redirect
  const from = location.state?.from?.pathname || '/';

  // Redirect if already authenticated
  useEffect(() => {
    if (isAuthenticated) {
      navigate(from, { replace: true });
    }
  }, [isAuthenticated, navigate, from]);

  // Clear auth errors when component mounts
  useEffect(() => {
    clearError();
  }, [clearError]);

  const { control, handleSubmit, formState: { errors }, setError } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      email: '',
      password: ''
    }
  });

  // ENHANCED: Submit function now passes charger parameters
  const onSubmit = async (data) => {
    try {
      clearError();
      console.log('🚀 Submitting login:', { 
        email: data.email, 
        isQrLogin, 
        chargerName, 
        connectorNumber 
      });
      
      // Call login with optional charger parameters
      const result = await login(
        data.email, 
        data.password, 
        isQrLogin ? chargerName : null, 
        isQrLogin ? connectorNumber : null
      );
      
      console.log('✅ Login result:', result);
      
      // Handle navigation based on login type
      if (result.success && result.redirectUrl) {
        // QR code login - redirect to specific charger
        console.log('🎯 Redirecting to charger:', result.redirectUrl);
        navigate(result.redirectUrl, { replace: true });
      } else if (result.success) {
        // Regular login - will be handled by useEffect above
        console.log('📱 Regular login completed');
      }
    } catch (err) {
      console.error('❌ Login error:', err);
      console.error('❌ Error response:', err.response?.data);
      
      // Handle specific error cases
      if (err.response?.status === 401) {
        setError('password', {
          type: 'manual',
          message: 'Invalid email or password'
        });
      } else if (err.response?.status === 403) {
        setError('email', {
          type: 'manual',
          message: err.response?.data?.detail || 'You don\'t have permission to access this charger'
        });
      } else if (err.response?.status === 429) {
        setError('email', {
          type: 'manual',
          message: 'Too many login attempts. Please try again later.'
        });
      } else {
        setError('email', {
          type: 'manual',
          message: err.response?.data?.detail || err.message || 'Login failed'
        });
      }
    }
  };

  const toggleShowPassword = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: (theme) => `linear-gradient(135deg, ${theme.palette.primary.light} 0%, ${theme.palette.secondary.main} 100%)`,
        p: 2
      }}
    >
      <Container maxWidth="sm">
        <Paper
          elevation={6}
          sx={{
            p: 4,
            borderRadius: (theme) => theme.shape.borderRadius * 2,
            background: (theme) => theme.palette.background.paper,
            boxShadow: (theme) => `0 4px 24px 0 ${theme.palette.primary.light}22`,
            backdropFilter: 'blur(10px)'
          }}
        >
          {/* Header */}
          <Box textAlign="center" mb={4}>
            <Box display="flex" justifyContent="center" mb={2}>
              <EvStationIcon 
                sx={{ 
                  fontSize: 48, 
                  color: 'primary.main',
                  filter: 'drop-shadow(0 2px 4px rgba(16,185,129,0.15))'
                }} 
              />
            </Box>
            <Typography variant="h4" component="h1" fontWeight="bold" gutterBottom color="primary.main">
              {isQrLogin ? 'Charger Access' : 'GetWatted CSMS'}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {isQrLogin ? 'Sign in to access the charger' : 'Sign in to your account'}
            </Typography>
          </Box>

          {/* NEW: QR Code Information Display */}
          {isQrLogin && (
            <Box mb={3}>
              {/* <Box display="flex" alignItems="center" justifyContent="center" mb={2}>
                <QrCodeIcon color="primary" sx={{ mr: 1 }} />
                <Typography variant="subtitle1" color="primary" fontWeight="medium">
                  QR Code Login
                </Typography>
              </Box> */}
              
              <Box 
                sx={{ 
                  bgcolor: 'primary.50', 
                  p: 2, 
                  borderRadius: 2,
                  border: '1px solid',
                  borderColor: 'primary.200',
                  textAlign: 'center'
                }}
              >
                <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                  <EvStationIcon fontSize="small" sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="body1" fontWeight="medium">
                    {chargerName}
                  </Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  Connector: {connectorNumber}
                </Typography>
              </Box>
              
              <Divider sx={{ my: 3 }} />
            </Box>
          )}

          {/* Error Alert */}
          {error && (
            <Alert severity="error" sx={{ mb: 3 }}>
              {error}
            </Alert>
          )}

          {/* Login Form */}
          <form onSubmit={handleSubmit(onSubmit)}>
            <Box mb={3}>
              <Controller
                name="email"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Email Address"
                    type="email"
                    fullWidth
                    error={!!errors.email}
                    helperText={errors.email?.message}
                    disabled={isLoading}
                    autoFocus={!isQrLogin}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <EmailIcon color="action" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: (theme) => theme.palette.background.default,
                      },
                    }}
                  />
                )}
              />
            </Box>

            <Box mb={3}>
              <Controller
                name="password"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    fullWidth
                    error={!!errors.password}
                    helperText={errors.password?.message}
                    disabled={isLoading}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <LockIcon color="action" />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={toggleShowPassword}
                            edge="end"
                            disabled={isLoading}
                          >
                            {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        background: (theme) => theme.palette.background.default,
                      },
                    }}
                  />
                )}
              />
            </Box>

            {/* Terms and Conditions Checkbox */}
            <Box mb={3}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={acceptedTerms}
                    onChange={(e) => setAcceptedTerms(e.target.checked)}
                    color="primary"
                    disabled={isLoading}
                  />
                }
                label={
                  <Typography variant="body2" color="text.secondary">
                    I accept the{' '}
                    <Link
                      href="/login/tsacs"
                      target="_blank"
                      rel="noopener noreferrer"
                      sx={{
                        color: 'primary.main',
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      }}
                    >
                      Terms & Conditions
                    </Link>
                  </Typography>
                }
                sx={{
                  alignItems: 'flex-start',
                  mt: 0
                }}
              />
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              disabled={isLoading || !acceptedTerms}
              sx={{
                py: 1.5,
                borderRadius: 2,
                fontSize: '1.1rem',
                fontWeight: 600,
                textTransform: 'none',
                background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.main} 30%, ${theme.palette.secondary.main} 90%)`,
                color: (theme) => theme.palette.primary.contrastText,
                boxShadow: (theme) => `0 2px 8px 0 ${theme.palette.primary.light}22`,
                '&:hover': {
                  background: (theme) => `linear-gradient(45deg, ${theme.palette.primary.dark} 30%, ${theme.palette.secondary.dark} 90%)`,
                },
              }}
            >
              {isLoading ? (
                <CircularProgress size={24} color="inherit" />
              ) : (
                isQrLogin ? 'Access Charger' : 'Sign In'
              )}
            </Button>
          </form>

          {/* Additional Information for QR Login */}
          {isQrLogin && (
            <Box mt={3} p={2} bgcolor="grey.50" borderRadius={2}>
              <Typography variant="caption" color="text.secondary" textAlign="center" display="block">
                🔒 This is a secure charger access login. Only authorized drivers can access this charger.
              </Typography>
            </Box>
          )}

          {/* Footer */}
          <Box textAlign="center" mt={3}>
            <Typography variant="body2" color="text.secondary">
              OCPP Management System v1.0
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default LoginPage;